/** @jsxImportSource @emotion/react */
import {
  Box,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Slide,
  useTheme,
} from '@mui/material'
import { Button } from '@app/components/Atoms/Button'
import { Icon } from '@app/components/Atoms/Icon'
import { propertyAssetTypeAtom, selectedAssetIdAtom } from '@app/state/propertyAtoms'
import { useAtomValue } from 'jotai'
import { useRef, useState } from 'react'
import { footerButtonsContainer, tooltipArrow } from './SlideInFooter.styles'
import { clientNameAtom } from '@app/state/clientsAtom'
import apiClient from '@app/config/axios.config'
import { pollForReport } from '@app/utils/pollForReport'
import { toast } from '@app/components/ui/Toast/toast'
import { userAtom } from '@app/hooks/useUserProfile'
import { logDataDogAction } from '@app/utils/logger'
import { DATADOG_ACTIONS } from '@app/app-constants'

interface SlideInFooterProps {
  open: boolean
}

export const SlideInFooter = ({ open }: SlideInFooterProps) => {
  const [exportMenuOpen, setExportMenuOpen] = useState(false)
  const propertyAssetType = useAtomValue(propertyAssetTypeAtom)
  const selectedAssetId = useAtomValue(selectedAssetIdAtom)
  const clientName = useAtomValue(clientNameAtom)
  const exportButtonRef = useRef<HTMLDivElement>(null)
  const theme = useTheme()
  const { data: user } = useAtomValue(userAtom)

  const handleClick = async () => {
    setExportMenuOpen(false)

    logDataDogAction(DATADOG_ACTIONS.EXPORT_REPORT, {
      type: 'natural-hazard-information',
      format: 'pdf',
    })

    toast({
      description:
        'Generating data for report. This may take a while, we will notify you when the report is ready.',
      type: 'info',
    })

    const report_id = `${user?.user_uuid}-${Date.now()}`
    try {
      await apiClient.post(
        `/report/export/${clientName}/natural-hazard-information-report/${report_id}`,
        { propertyAssetType, selectedAssetId },
      )

      pollForReport(report_id, 'Urban Intelligence Property Report.pdf')
    } catch (error) {
      toast({
        description:
          'There was an error generating the report. Please notify your Urban Intelligence contact or submit feedback.',
        type: 'error',
      })
    }
  }

  return (
    <Slide direction="up" in={open}>
      <Box css={footerButtonsContainer(theme)}>
        <div ref={exportButtonRef}>
          <Button variant="contained" onClick={() => setExportMenuOpen((prev) => !prev)}>
            Export <Icon iconName="ChevronRight" colour="#FFF" />
          </Button>
        </div>

        <Popper
          open={exportMenuOpen}
          anchorEl={exportButtonRef.current}
          role={undefined}
          transition
          sx={{ zIndex: (theme) => theme.zIndex.tooltip }}
          placement="right-end"
          modifiers={[
            {
              name: 'flip',
              enabled: false,
              options: {
                altBoundary: true,
                rootBoundary: 'document',
                padding: 8,
              },
            },
            {
              name: 'preventOverflow',
              enabled: false,
              options: {
                altAxis: true,
                altBoundary: true,
                tether: true,
                rootBoundary: 'document',
                padding: 8,
              },
            },
            {
              name: 'offset',
              options: {
                offset: [0, 16],
              },
            },
          ]}
        >
          {({ TransitionProps }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: 'left bottom',
              }}
            >
              <Paper
                css={{
                  border: '2px solid',
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                }}
              >
                <span css={tooltipArrow(theme.palette.primary.main)} className="arrow" />
                <ClickAwayListener onClickAway={() => setExportMenuOpen(false)}>
                  <MenuList autoFocusItem={exportMenuOpen} id="menu-list-grow">
                    <Box>
                      <MenuItem onClick={handleClick}>Report (.pdf)</MenuItem>
                    </Box>
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </Box>
    </Slide>
  )
}
